import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Link, useHistory } from 'react-router-dom';

import useI18n from 'i18n';
import Config from 'config';
import { useBridgeApi, useDispatch, useSelector, useLogger } from 'Hooks';
import { setAlert } from 'actions';
import TextField from '../../Components/TextField';
import Button from '../../Components/Button';
import { GetUser } from './__queries__';
import { useStorage } from 'Components/Storage';
import { getDeviceSize, DEVICE_SIZES } from '../../utils/deviceSize';

const Wrapper = styled.div`
  padding: 120px 24px 88px;
  animation: fade-in 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const Inner = styled.div`
  @media (min-width: 768px) {
    width: 417px;
    margin: 0 auto;
  }
`;

const ForgotPasswordLink = styled(Link)`
  color: #212124;
  float: right;
  text-decoration: underline;

  @media (min-width: 768px) {
    margin: 0 0 24px 0;
  }
`;

export const SIGN_IN_MUTATION = gql`
  mutation login($companyId: ID!, $phone: String!, $password: String!) {
    login(companyId: $companyId, phone: $phone, password: $password) {
      error
      token
    }
  }
`;
const USER_QUERY = gql`
  query GetUser {
    user {
      id
      activated
    }
  }
`;

const rules = [
  {
    name: 'phone',
    message: 'SignUp.Error.Phone',
  },
  {
    name: 'password',
    regexp: /^.{4,}$/,
    message: 'SignUp.Error.Password',
  },
];

const defaultFormData = {
  phone: '',
  password: '',
};
const SignIn = () => {
  const logger = useLogger('sign-in');
  const [storage, updateStorage] = useStorage();
  const { i18n } = useI18n();
  const params = new URLSearchParams(window.location.search);
  const venueId = params.get('v') || '';
  const tableId = useSelector((state) => state.tableId ?? '');
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { redirectPath } = useSelector((state) => state);
  const api = useBridgeApi();

  const [formData, setFormData] = useState<{ [key: string]: string }>({
    phone: Config.PHONE_COUNTRY_CODE ?? '+46',
    password: '',
  });
  const [errors, setErrors] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);
  const deviceSize = getDeviceSize();

  const [signIn, { data }] = useMutation(SIGN_IN_MUTATION, {
    variables: {
      companyId: Config.COMPANY_ID,
      phone: formData.phone,
      password: formData.password,
    },
  });
  const [getUserData] = useLazyQuery<GetUser>(USER_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (props) => handleGetUserData(props),
  });

  const clearStoredToken = () => {
    updateStorage({ authToken: null, temporaryAuthToken: null });
  };

  const handleGetUserData = (userData: any) => {
    const token = data.login.token!;

    try {
      if (!userData?.user) {
        logger.warn('Sign in failed, no user data', { token });
        if (storage.authToken || storage.temporaryAuthToken) {
          clearStoredToken();
        }
        throw new Error('UNEXPECTED_ERROR_OCCURRED');
      }

      let redirectPathUrl = '';
      if (userData.user.activated) {
        updateStorage({ authToken: token });

        // If we are in QR mode, and the user is activated, take them to home.
        if (tableId && venueId) {
          redirectPathUrl = '/';
        } else {
          redirectPathUrl = redirectPath;
        }
      } else {
        updateStorage({ temporaryAuthToken: token });
        redirectPathUrl = 'verify-account';
      }
      api.vibrate('impactLight');
      setLoading(false);
      logger.info('Sign in succesful!', {
        user: userData?.user,
      });
      // Route the user to the specified path.
      push(redirectPathUrl);
    } catch (err: any) {
      console.log('successSignIn error', err);
      logger.warn('Sign in failed!', {
        description: err.message,
      });
      dispatch(
        setAlert({
          title: 'SignIn.Error.Failed',
          description: i18n(`Error.${err.message}`),
        }),
      );
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = handleValidate();
    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      const response = await signIn();

      const { error, token } = response?.data?.login;

      if (!error && !token) {
        setLoading(false);
        return;
      }

      if (error || !token) {
        throw new Error(error);
      }
      updateStorage({ temporaryAuthToken: token });

      // Wait for dispatch to go through
      await new Promise((resolve) => {
        setTimeout(resolve, 1);
      });

      await getUserData();
    } catch (err: any) {
      logger.warn('handleSignIn error', { error: err });
      dispatch(
        setAlert({
          title: i18n('SignIn.Error.Failed'),
          description: i18n(`Error.${err.message}`),
        }),
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    setFormData({
      phone: '+46',
      password: '',
    });
    setErrors(defaultFormData);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleValidate = () => {
    const messages: { [key: string]: string } = { ...errors };

    rules.forEach((field) => {
      let valid = false;

      if (field.name === 'phone') {
        const phoneNumber = parsePhoneNumberFromString(formData[field.name]);
        valid = Boolean(phoneNumber && phoneNumber.isValid());
      } else if (field.regexp) {
        valid = field.regexp.test(formData[field.name]);
      }
      messages[field.name] = valid ? '' : i18n(field.message);
    });

    setErrors((prev) => ({ ...prev, ...messages }));
    return Object.values(messages).every((el) => !el);
  };

  return (
    <Wrapper>
      <Inner>
        <form onSubmit={handleSignIn}>
          <TextField
            label={i18n('Input.Phone')}
            placeholder={i18n('Input.Phone.Placeholder')}
            onChange={handleChange}
            name="phone"
            value={formData.phone.replace(/\s/g, "")}
            type="tel"
            error={errors.phone}
          />
          <TextField
            label={i18n('Signin.Password')}
            placeholder={i18n('Signin.Password.Placeholder')}
            onChange={handleChange}
            value={formData.password}
            name="password"
            type="password"
            error={errors.password}
          />
          <ForgotPasswordLink to="/forgot-password">
            {i18n('ForgotPassword.Text')}
          </ForgotPasswordLink>
          <Button
            text={i18n(loading ? 'General.Loading' : 'Signin.Title')}
            floating={deviceSize === DEVICE_SIZES.SMALL}
            width="100%"
            type="submit"
          />
        </form>
      </Inner>
    </Wrapper>
  );
};

export default SignIn;
